/**
 * What `POST /api/route/check` answers, as something a pilot can read.
 *
 * The endpoint returns openAIP's own vocabulary — `kind` is a slug (`CTR`, `R`,
 * `TMA`), `icao_class` a single letter, the limits both as feet and as the raw
 * published string. Three rules turn that into rows:
 *
 * **The kind decides the colour, and the kind is grouped, not listed.** A pilot
 * scanning the list needs two questions answered: does this one need a call, and
 * is this one a place I may not be. Controlled airspace is blue (`--ae-info`),
 * anything restricted, danger, prohibited or reserved is amber (`--ae-caution`),
 * everything else carries no colour at all. Colour is never the only carrier —
 * the kind is spelled out in words beside the dot.
 *
 * **The abbreviation stays.** `CTR`, `TMA`, `RMZ` are what is printed on the
 * chart and said on the radio; translating them would be inventing vocabulary.
 * What is translated is the word BESIDE them, and only for the kinds whose
 * letter alone says nothing (`R`, `D`, `P`). Every catalogue key is written out
 * as a literal in {@link AIRSPACE_KIND_KEY}, never assembled from the slug — a
 * built key is a key the catalogue's own dead-key test cannot see.
 *
 * **The published limit wins over the computed one.** `lower_raw` is what the
 * AIP says (`GND`, `FL 65`, `2500 MSL`); `lower_ft` is our reading of it. Where
 * both exist the pilot sees the AIP's words, because that is what they will
 * compare against the chart.
 *
 * Pure: no React, no fetch. `useAirspace.ts` owns the call.
 */

import type { CheckedAirspace, CheckedLeg, RouteCheck } from '../../api';
import { feetText } from './format';

/** How a crossing is coloured: a call, a restriction, or neither. */
export type AirspaceGroup = 'controlled' | 'restricted' | 'other';

/** Kinds that need a clearance or at least a call before entering. */
const CONTROLLED = new Set([
  'CTR', 'MCTR', 'TMA', 'CTA', 'ATZ', 'MATZ', 'AWY', 'LTA', 'UTA', 'ACC',
]);

/** Kinds that are closed, dangerous or reserved for someone else. */
const RESTRICTED = new Set([
  'R', 'D', 'P', 'TRA', 'TSA', 'ALERT', 'WARNING', 'PROTECTED', 'MTA', 'MTR',
  'MRT', 'OFR', 'TFR',
]);

export const airspaceGroup = (kind: string): AirspaceGroup =>
  RESTRICTED.has(kind) ? 'restricted' : CONTROLLED.has(kind) ? 'controlled' : 'other';

/**
 * The word beside the abbreviation, per kind. Kinds not in the table are shown
 * as their abbreviation alone, which is what a chart prints for them too.
 */
export const AIRSPACE_KIND_KEY: Readonly<Record<string, string>> = {
  R: 'terrain.as.R',
  D: 'terrain.as.D',
  P: 'terrain.as.P',
  CTR: 'terrain.as.CTR',
  MCTR: 'terrain.as.MCTR',
  TMA: 'terrain.as.TMA',
  CTA: 'terrain.as.CTA',
  ATZ: 'terrain.as.ATZ',
  MATZ: 'terrain.as.MATZ',
  TMZ: 'terrain.as.TMZ',
  RMZ: 'terrain.as.RMZ',
  TRA: 'terrain.as.TRA',
  TSA: 'terrain.as.TSA',
  ALERT: 'terrain.as.ALERT',
  WARNING: 'terrain.as.WARNING',
  PROTECTED: 'terrain.as.PROTECTED',
  GLIDING: 'terrain.as.GLIDING',
  SPORT: 'terrain.as.SPORT',
  HTZ: 'terrain.as.HTZ',
  TIZ: 'terrain.as.TIZ',
  TIA: 'terrain.as.TIA',
  AWY: 'terrain.as.AWY',
  ADIZ: 'terrain.as.ADIZ',
};

/**
 * One vertical limit as the pilot should read it: the AIP's own words where
 * there are any, our reading of them where there are not, `?` where neither
 * exists (which happens, and is more honest than a 0 that looks like GND).
 */
export const limitText = (raw: string | null, ft: number | null): string =>
  raw && raw.trim() !== '' ? raw.trim() : ft == null ? '?' : feetText(ft);

/**
 * `GND – 2 500 ft` — an en dash between thin spaces, which is what a RANGE is
 * set with. A hyphen would read as a minus in a column of altitudes.
 */
export const RANGE_SEP = '\u2009\u2013\u2009';

export const limitsText = (a: CheckedAirspace): string =>
  limitText(a.lower_raw, a.lower_ft) + RANGE_SEP + limitText(a.upper_raw, a.upper_ft);

/** One crossing, ready to render. */
/**
 * openAIP's catch-all. It is a real slug and it is on real class-E areas, but
 * „OTHER" is not a word a pilot reads on a chart: the row shows the class and
 * the name instead, which is what those areas are actually identified by.
 */
const NO_ABBR = new Set(['OTHER']);

export interface AirspaceRow {
  id: string;
  /** the FORM's leg number, 1-based */
  leg: number;
  name: string;
  kind: string;
  /** the abbreviation as it is printed on a chart, or '' where there is none */
  abbr: string;
  /** the catalogue key for the word beside the abbreviation, or '' */
  kindKey: string;
  icaoClass: string | null;
  group: AirspaceGroup;
  limits: string;
  /** where the route enters it, cumulative NM — null when the endpoint says nothing */
  entryNm: number | null;
  /** the same point measured from the START OF ITS OWN LEG, which is what the map needs */
  entryLegNm: number | null;
}

/** One leg's crossings, in the order the route meets them. */
export interface AirspaceLeg {
  /** the FORM's leg number, 1-based */
  leg: number;
  distNm: number;
  rows: AirspaceRow[];
}

const rowsOf = (leg: CheckedLeg, startNm: number): AirspaceRow[] =>
  [...leg.airspaces]
    .sort((a, b) => (a.entry_nm ?? 0) - (b.entry_nm ?? 0))
    .map(a => ({
      id: a.id,
      leg: leg.leg + 1,
      name: a.name,
      kind: a.kind,
      abbr: NO_ABBR.has(a.kind) ? '' : a.kind,
      kindKey: AIRSPACE_KIND_KEY[a.kind] ?? '',
      icaoClass: a.icao_class,
      group: airspaceGroup(a.kind),
      limits: limitsText(a),
      entryNm: a.entry_nm == null ? null : Math.round((startNm + a.entry_nm) * 10) / 10,
      entryLegNm: a.entry_nm,
    }));

/** The check, grouped by leg, with cumulative entry distances. */
export function airspaceLegs(check: RouteCheck | null | undefined): AirspaceLeg[] {
  if (!check) return [];
  let start = 0;
  return check.legs.map(l => {
    const rows = rowsOf(l, start);
    start += l.dist_nm || 0;
    return { leg: l.leg + 1, distNm: Math.round((l.dist_nm || 0) * 10) / 10, rows };
  });
}

/** How many DISTINCT areas of each kind the whole route crosses. */
export function airspaceCounts(legs: readonly AirspaceLeg[]): {
  controlled: number;
  restricted: number;
  total: number;
} {
  const seen = new Map<string, AirspaceGroup>();
  for (const l of legs) for (const r of l.rows) seen.set(r.id, r.group);
  let controlled = 0;
  let restricted = 0;
  for (const g of seen.values()) {
    if (g === 'controlled') controlled++;
    else if (g === 'restricted') restricted++;
  }
  return { controlled, restricted, total: seen.size };
}
